import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { User } from '@esg/esg-global-types';
import { MetadataError } from '@ep/error-handling';

import { UserContext } from '../../../context/UserContext';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import type { UserStatus } from '../../../util/user_status';
import { getUsersPage, type GetUsersPageFilters } from '../../../graphql/services/user.service';
import { GROUP_ADMIN_ROLE_FILTER, SUPER_ADMIN_ROLE_FILTER } from '../user_filter_constants';

export interface UsersTableFilters {
  search: string;
  role: string | 'all';
  company_id: string | 'all';
  group_id: string | 'all';
  status: UserStatus | 'all';
  type: User['type'] | 'all';
  show_deleted: boolean;
}

export const INITIAL_FILTERS: UsersTableFilters = {
  search: '',
  role: 'all',
  company_id: 'all',
  group_id: 'all',
  status: 'all',
  type: 'all',
  show_deleted: false
};

const PAGE_SIZE = 25;

/**
 * Maps the list-view's UI filters onto the server query's filter shape.
 * `super_admin` / `is_group_admin` are dedicated sentinel fields (mirroring
 * the backend's `applyUserFilters`) rather than looked up in the company-role
 * `role` array, since super-admin/group-admin are access flags, not roles.
 */
const toApiFilters = (filters: UsersTableFilters): GetUsersPageFilters => {
  const api: GetUsersPageFilters = { include_deleted: filters.show_deleted };

  const search = filters.search.trim();
  if (search.length > 0) api.search = search;

  if (filters.role === SUPER_ADMIN_ROLE_FILTER) {
    api.super_admin = true;
  } else if (filters.role === GROUP_ADMIN_ROLE_FILTER) {
    api.is_group_admin = true;
  } else if (filters.role !== 'all') {
    api.role = filters.role;
  }

  if (filters.company_id !== 'all') api.company_id = filters.company_id;
  if (filters.group_id !== 'all') api.group_id = filters.group_id;
  if (filters.status !== 'all') api.status = filters.status;
  if (filters.type !== 'all') api.type = filters.type;

  return api;
};

export interface UseUsersTableOptions {
  /**
   * Bumped by the host after an out-of-band mutation (Add User, CSV import)
   * that should jump the view back to page 1 so the new/changed rows are
   * visible under the default created-DESC ordering.
   */
  refreshSignal?: number;
}

/**
 * Server-driven state machine for the user list view: filters, cursor
 * pagination, and a selection that survives filter/page changes by holding
 * full `User` objects (not just ids) — a selected user who scrolls off the
 * loaded page is kept (pinned) in the rendered rows instead of disappearing.
 */
export const useUsersTable = (options: UseUsersTableOptions = {}) => {
  const { refreshSignal } = options;
  const { user } = useContext(UserContext);
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);

  const [filters, setFilters] = useState<UsersTableFilters>(INITIAL_FILTERS);
  const [rows, setRows] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selection, setSelection] = useState<Map<string, User>>(new Map());

  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const requestIdRef = useRef(0);

  const setFilter = useCallback(
    <K extends keyof UsersTableFilters>(key: K, value: UsersTableFilters[K]) =>
      setFilters((prev) => ({ ...prev, [key]: value })),
    []
  );

  const fetchPage = useCallback(
    async (cursor: string | null, targetPageIndex: number) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      try {
        const page = await getUsersPage(toApiFilters(filtersRef.current), cursor, PAGE_SIZE);
        if (requestId !== requestIdRef.current) return; // superseded by a later request
        setRows(page.users);
        setNextCursor(page.nextCursor);
        setPageIndex(targetPageIndex);
      } catch (err: unknown) {
        if (requestId !== requestIdRef.current) return;
        const trackingId = generateUuidV4();
        log(
          'error',
          new MetadataError(
            err instanceof Error
              ? err.message
              : 'Error: useUsersTable failed on an unknown error while fetching users.',
            { user },
            trackingId
          )
        );
        setFeedbackData({
          message: `Unable to fetch user list. Tracking ID: ${trackingId}`,
          state: true,
          type: 'error'
        });
        setRows([]);
        setNextCursor(null);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    [user, setFeedbackData]
  );

  // Refetch page 1 whenever a filter changes — debounced only when the free-text
  // `search` box itself changed (typing), immediate for discrete filter picks
  // (role/company/group/status/type/show_deleted), which are deliberate,
  // one-off actions rather than a stream of keystrokes.
  const prevSearchRef = useRef(filters.search);
  useEffect(() => {
    const searchChanged = prevSearchRef.current !== filters.search;
    prevSearchRef.current = filters.search;
    const delay = searchChanged ? 300 : 0;
    const timer = setTimeout(() => {
      setCursorStack([null]);
      void fetchPage(null, 0);
    }, delay);
    return () => clearTimeout(timer);
    // fetchPage reads the latest filters via filtersRef, so it deliberately
    // isn't a dependency here — only the filter *values* should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.search,
    filters.role,
    filters.company_id,
    filters.group_id,
    filters.status,
    filters.type,
    filters.show_deleted
  ]);

  // Host-triggered refresh (e.g. after Add User / CSV import) — jump back to
  // page 1. Skipped on mount: the filters-changed effect above already fetches
  // the first page then, so also firing here would double-fetch.
  const isFirstRefreshSignalRender = useRef(true);
  useEffect(() => {
    if (isFirstRefreshSignalRender.current) {
      isFirstRefreshSignalRender.current = false;
      return;
    }
    if (refreshSignal === undefined) return;
    setCursorStack([null]);
    void fetchPage(null, 0);
    // fetchPage reads the latest filters via filtersRef, so it deliberately
    // isn't a dependency here — this effect should only re-run on a new
    // refresh signal, not on every filter change (the effect above owns that).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const hasNextPage = nextCursor !== null;
  const hasPrevPage = pageIndex > 0;

  const goToNextPage = useCallback(() => {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev.slice(0, pageIndex + 1), nextCursor]);
    void fetchPage(nextCursor, pageIndex + 1);
  }, [nextCursor, pageIndex, fetchPage]);

  const goToPrevPage = useCallback(() => {
    if (pageIndex === 0) return;
    void fetchPage(cursorStack[pageIndex - 1], pageIndex - 1);
  }, [pageIndex, cursorStack, fetchPage]);

  /** Refetches the current page in place (e.g. after a bulk action) without moving pages. */
  const refetchCurrentPage = useCallback(() => {
    void fetchPage(cursorStack[pageIndex], pageIndex);
  }, [cursorStack, pageIndex, fetchPage]);

  /**
   * Patches a single row in place (optimistic update after a single-row
   * mutation), keeping the selection map's copy of that row in sync too.
   */
  const patchRow = useCallback((id: string, apply: (row: User) => User) => {
    setRows((prev) => prev.map((row) => (row.id === id ? apply(row) : row)));
    setSelection((prev) => {
      const existing = prev.get(id);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(id, apply(existing));
      return next;
    });
  }, []);

  // Selected users who scrolled off the current page (filter changed, or they
  // were on a different page) are pinned back into the rendered rows so they
  // stay visible — and, since the DataGrid renders from this list, checked.
  const renderedRows = useMemo(() => {
    const pinned = [...selection.values()].filter((u) => !rows.some((row) => row.id === u.id));
    return [...pinned, ...rows];
  }, [selection, rows]);

  const setSelectionFromIds = useCallback(
    (ids: string[]) => {
      setSelection((prev) => {
        const lookup = new Map(renderedRows.map((u) => [u.id, u]));
        const next = new Map<string, User>();
        ids.forEach((id) => {
          const found = prev.get(id) ?? lookup.get(id);
          if (found) next.set(id, found);
        });
        return next;
      });
    },
    [renderedRows]
  );

  const clearSelection = useCallback(() => setSelection(new Map()), []);

  return {
    filters,
    setFilter,
    rows: renderedRows,
    loading,
    hasNextPage,
    hasPrevPage,
    pageIndex,
    goToNextPage,
    goToPrevPage,
    refetchCurrentPage,
    patchRow,
    selection,
    setSelectionFromIds,
    clearSelection
  };
};
